/**
 * NotificationDropdown Component
 * แสดง dropdown รายการ notifications พร้อมปุ่ม "ดูทั้งหมด" และ "ทำเครื่องหมายว่าอ่านทั้งหมด"
 */

import React, { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { CheckIcon, EyeIcon } from '@heroicons/react/24/outline'
import NotificationItem from './NotificationItem'
import { useNotifications } from '../../hooks/useNotifications'
import { notificationService } from '../../services/notificationService'
import { Notification } from '../../types/notification'

interface NotificationDropdownProps {
  isOpen: boolean
  onClose: () => void
  maxItems?: number
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onClose,
  maxItems = 5
}) => {
  const navigate = useNavigate()
  const {
    notifications,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearError
  } = useNotifications()

  // DEBUG: Log isOpen prop
  console.log('NotificationDropdown render:', { isOpen, notifications: notifications?.length })

  // Fetch notifications เมื่อ dropdown เปิด
  // FIX: ลบ fetchNotifications จาก dependencies เพื่อหลีกเลี่ยง infinite loop
  // fetchNotifications ถูก memoize ด้วย useCallback ใน useNotifications แล้ว
  useEffect(() => {
    console.log('NotificationDropdown useEffect:', { isOpen, maxItems })
    if (isOpen) {
      fetchNotifications(1, maxItems, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, maxItems])

  // Handle notification item click
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.is_read) {
      await markAsRead(notification.id)
    }

    // Navigate to link
    if (notification.link) {
      const path = notificationService.parseNotificationLink(notification.link)
      navigate(path)
    }

    // Close dropdown
    onClose()
  }

  // Handle "Mark all as read"
  const handleMarkAllAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation()
    await markAllAsRead()
  }

  // Handle "View all"
  const handleViewAll = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate('/notifications')
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="absolute right-0 mt-3 w-96 bg-white rounded-xl shadow-xl border border-gray-200/60 z-50 backdrop-blur-sm overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">การแจ้งเตือน</h3>
            {notifications && notifications.length > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center space-x-1 hover:underline"
              >
                <CheckIcon className="w-3.5 h-3.5" />
                <span>อ่านทั้งหมด</span>
              </button>
            )}
          </div>
          </div>

          {/* Content */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            {error && (
              <div className="px-4 py-3">
                <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3">
                  {error}
                </div>
              </div>
            )}

            {!loading && !error && (!notifications || notifications.length === 0) && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                </div>
                <p className="text-sm text-gray-600 font-medium">ไม่มีการแจ้งเตือนใหม่</p>
                <p className="text-xs text-gray-500 mt-1">คุณได้อ่านการแจ้งเตือนทั้งหมดแล้ว</p>
              </div>
            )}

            {!loading && !error && notifications && notifications.length > 0 && (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div key={notification.id} className="px-2 py-1">
                    <NotificationItem
                      notification={notification}
                      onClick={handleNotificationClick}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {!loading && notifications && notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gradient-to-r from-white to-gray-50">
              <button
                onClick={handleViewAll}
                className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center space-x-2 hover:underline"
              >
                <EyeIcon className="w-4 h-4" />
                <span>ดูทั้งหมด</span>
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default NotificationDropdown
